import { Box, HStack, Text } from "@chakra-ui/react";
import React from "react";
import { WarningFillIcon } from "../../styles/icons";
import { SpamWarningLevel } from "../../hooks/useSpamWarning";
import { FilterMode } from "./types";

interface SpamWarningBannerProps {
  level: SpamWarningLevel;
  totalCount: number;
  scheduledCount: number;
  sentCount: number;
  filterMode: FilterMode;
}

interface BannerTheme {
  bg: string;
  border: string;
  iconColor: string;
  subMessage: string;
}

const getBannerTheme = (level: SpamWarningLevel): BannerTheme | null => {
  switch (level) {
    case "soft":
      return {
        bg: "spamWarning.soft.bg",
        border: "spamWarning.soft.border",
        iconColor: "spamWarning.soft.icon",
        subMessage: "Consider spacing messages to maintain engagement.",
      };
    case "strong":
      return {
        bg: "spamWarning.strong.bg",
        border: "spamWarning.strong.border",
        iconColor: "spamWarning.strong.icon",
        subMessage:
          "Consider rescheduling or refining your audience to avoid disengagement.",
      };
    case "highRisk":
      return {
        bg: "spamWarning.highRisk.bg",
        border: "spamWarning.highRisk.border",
        iconColor: "spamWarning.highRisk.icon",
        subMessage:
          "We strongly recommend rescheduling to protect member engagement.",
      };
    default:
      return null;
  }
};

export const SpamWarningBanner: React.FC<SpamWarningBannerProps> = ({
  level,
  totalCount,
  scheduledCount,
  sentCount,
  filterMode,
}) => {
  const theme = getBannerTheme(level);
  if (!theme) return null;

  const countParts: string[] = [];
  if (scheduledCount > 0) {
    countParts.push(`${scheduledCount} Scheduled (next 7 days)`);
  }
  if (sentCount > 0) {
    countParts.push(`${sentCount} Sent (last 7 days)`);
  }

  return (
    <Box
      bg={theme.bg}
      border="1px solid"
      borderColor={theme.border}
      borderRadius="md"
      p={3}
      mt={1}
    >
      <HStack spacing={2} alignItems="flex-start">
        <WarningFillIcon
          color={theme.iconColor}
          boxSize={5}
          flexShrink={0}
          mt={0.5}
        />
        <Box>
          <Text fontSize="sm" color="gray.700">
            {filterMode === "manualFilter"
              ? "These filtered members have"
              : filterMode === "segment"
              ? "Members in this segment have"
              : "All members have"}{" "}
            received{" "}
            <Text as="span" fontWeight="bold">
              {totalCount} notifications
            </Text>{" "}
            sent or scheduled within a short period. {theme.subMessage}
          </Text>
          {countParts.length > 0 && (
            <Text fontSize="sm" color="gray.700" fontWeight="bold" mt={1}>
              {countParts.join(" \u2022 ")}
            </Text>
          )}
        </Box>
      </HStack>
    </Box>
  );
};
